export interface PostMeta {
    slug: string;
    title: string;
    description: string;
    author: string;
    date: string;
    tags?: string[];
    cover?: string;
    readingTime: string;
}

type ModuleGlob = Record<string, { metadata: Record<string, unknown>; default: unknown }>;
type RawGlob = Record<string, string>;

function estimateReadingTime(text: string): string {
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.max(1, Math.round(words / 225));
    return `${minutes} min read`;
}

export async function getAllPosts(): Promise<PostMeta[]> {
    const modules = import.meta.glob('/src/posts/*.md', { eager: true }) as ModuleGlob;
    const raws = import.meta.glob('/src/posts/*.md', {
        eager: true,
        query: '?raw',
        import: 'default'
    }) as unknown as RawGlob;

    const posts: PostMeta[] = Object.entries(modules).map(([path, mod]) => {
        const slug = path.split('/').pop()!.replace(/\.md$/, '');
        const meta = mod.metadata as Omit<PostMeta, 'slug' | 'readingTime'>;
        const raw = raws[path] ?? '';
        const body = raw.replace(/^---[\s\S]*?---/, '');

        return {
            slug,
            title: meta.title,
            description: meta.description,
            author: meta.author,
            date: meta.date,
            tags: meta.tags ?? [],
            cover: meta.cover,
            readingTime: estimateReadingTime(body)
        };
    });

    posts.sort((a, b) => +new Date(b.date) - +new Date(a.date));
    return posts;
}

export async function getLatestPosts(n = 3): Promise<PostMeta[]> {
    const all = await getAllPosts();
    return all.slice(0, n);
}
